import { Button, Group, Paper, Stack, Text } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useUserConsent } from '../../../hooks/useUserConsent.ts';
import { useSessionProvider } from '../../../provider/sessionContext.tsx';
import CookiePreferencesModal from '../CookiePreferencesModal/CookiePreferencesModal.tsx';
import classes from './CookieConsentBanner.module.less';

const CookieConsentBanner: React.FC = () => {
    const { t } = useTranslation();
    const { needsConsent, saveConsent, isLoading } = useUserConsent();
    const { currentUser } = useSessionProvider();
    const location = useLocation();
    const [showBanner, setShowBanner] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);

    const isPublicPage = ['/login', '/register', '/verify-email'].some(
        path => location.pathname === path || location.pathname.startsWith(path)
    );

    useEffect(() => {
        try {
            // Show banner if user is logged in with a complete profile, not on public page, and needs consent
            // Don't wait for API sync to complete - show immediately if no local consent
            const shouldShow = Boolean(!isPublicPage && currentUser?.hasProfile && needsConsent && !isLoading);

            setShowBanner(shouldShow);
        } catch (error) {
            // Silently fail - don't block the app if banner logic fails
            console.error('Error in cookie consent banner:', error);
            setShowBanner(false);
        }
    }, [needsConsent, isLoading, currentUser, isPublicPage]);

    const handleAcceptAll = async () => {
        await saveConsent({
            essentialCookies: true,
            analyticsCookies: true,
        });
        setShowBanner(false);
    };

    const handleRejectAll = async () => {
        await saveConsent({
            essentialCookies: true,
            analyticsCookies: false,
        });
        setShowBanner(false);
    };

    const handleCustomize = () => {
        setShowPreferencesModal(true);
    };

    const handlePreferencesSaved = () => {
        setShowBanner(false);
        setShowPreferencesModal(false);
    };

    if (!showBanner)
        return null;

    return (
        <>
            <Paper 
                shadow="xl" 
                p="xl" 
                className={classes.chatContainer}
                style={{
                    maxWidth: '500px',
                    border: '2px solid #228be6',
                }}
            >
                <Stack gap="lg">
                    <Text size="xl" fw={700} c="dark">
                        {t('cookieConsent.banner.title')}
                    </Text>
                    <Text size="md" c="dark" lh={1.6}>
                        {t('cookieConsent.banner.description')}
                    </Text>
                    <Stack gap="sm" mt="xs">
                        <Button 
                            size="md" 
                            onClick={handleAcceptAll}
                            fullWidth
                            styles={{
                                root: {
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    height: '48px',
                                },
                            }}
                        >
                            {t('cookieConsent.banner.acceptAll')}
                        </Button>
                        <Group gap="sm" grow>
                            <Button 
                                variant="default" 
                                size="md" 
                                onClick={handleCustomize}
                                styles={{
                                    root: {
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        height: '44px',
                                    },
                                }}
                            >
                                {t('cookieConsent.banner.customize')}
                            </Button>
                            <Button 
                                variant="default" 
                                size="md" 
                                onClick={handleRejectAll}
                                styles={{
                                    root: {
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        height: '44px',
                                    },
                                }}
                            >
                                {t('cookieConsent.banner.rejectAll')}
                            </Button>
                        </Group>
                    </Stack>
                </Stack>
            </Paper>
            <CookiePreferencesModal
                opened={showPreferencesModal}
                onClose={() => setShowPreferencesModal(false)}
                onSave={handlePreferencesSaved}
            />
        </>
    );
};

export default CookieConsentBanner;
